import React, { useState, Suspense } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import TablePageSkeleton from '../components/TablePageSkeleton';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    List,
    Typography,
    Divider,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Avatar,
    Badge,
    alpha,
    useTheme,
    useMediaQuery,
    Menu,
    MenuItem,
    Tooltip,
    Chip,
    Button
} from '@mui/material';
import {
    Logout as LogoutIcon,
    Notifications as BellIcon,
    Menu as MenuIcon,
    ChevronLeft as ChevronLeftIcon,
    ArrowBack as ArrowBackIcon,
    GridView as GridViewIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { useCategory } from '../context/CategoryContext';
import { ALL_MENU_ITEMS, CATEGORY_LABELS, findMenuItemForPath } from '../config/categoryConfig';
import { canOpenPath, isSuperAdmin, menuRank, rolesLabel } from '../config/adminAccess';
import { getAdminNotifications, markAdminNotificationRead } from '../services/api';
import yannLogo from '../assets/logo/logo_extracted.png';

// Simple native replacement for date-fns formatDistanceToNow
const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
};

const drawerWidth = 260;

/* Menu items & category labels imported from shared config */

const DashboardLayout = () => {
    const { logout, admin, roles } = useAuth();
    const superAdmin = isSuperAdmin(roles);
    const roleLabel = rolesLabel(roles);
    // The person's name (Team page, saved at login); logins without one show the role instead, as before.
    const personName: string | null = admin?.name || null;
    const roleInitials = (personName || roleLabel).split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    const { activeCategory, setActiveCategory, clearCategory } = useCategory();

    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
    const [notifications, setNotifications] = useState<any[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const { socket } = useSocket();

    // Keep activeCategory in sync with the current URL. sessionStorage only records
    // the category from the last CategoryHub card click, so on a hard reload it can
    // be stale (e.g. still "outstation" from earlier) while the URL you're actually
    // on belongs to a different category — that mismatch used to silently redirect
    // nowhere useful. Deriving the category from the route itself on every load fixes
    // that; we only fall back to the category picker when the route itself is unknown
    // AND there's no stored category to fall back on.
    React.useEffect(() => {
        const routeCategory = findMenuItemForPath(location.pathname)?.category;
        if (routeCategory && routeCategory !== activeCategory) {
            setActiveCategory(routeCategory);
        } else if (!routeCategory && !activeCategory) {
            navigate('/categories');
        }
    }, [location.pathname, activeCategory, setActiveCategory, navigate]);

    // Fetch initial notifications (super-admin API — a marketing login has no bell)
    React.useEffect(() => {
        if (!superAdmin) return;
        const fetchInitial = async () => {
            try {
                const data = await getAdminNotifications(25, 0);
                setNotifications(data.notifications || []);
                setUnreadCount(data.notifications?.filter((n: any) => !n.is_read).length || 0);
            } catch (err) {
                console.error('Failed to fetch initial notifications:', err);
            }
        };
        fetchInitial();
    }, [superAdmin]);

    // Socket Listener
    React.useEffect(() => {
        if (!socket) return;

        const handleNewNotification = (notif: any) => {
            setNotifications(prev => [notif, ...prev].slice(0, 25));
            if (!notif.is_read) setUnreadCount(prev => prev + 1);
        };

        socket.on('new_admin_notification', handleNewNotification);
        return () => {
            socket.off('new_admin_notification', handleNewNotification);
        };
    }, [socket]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleNotifMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setNotifAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setNotifAnchorEl(null);
    };

    const handleNotifClick = async (notif: any) => {
        handleMenuClose();
        if (!notif.is_read) {
            try {
                await markAdminNotificationRead(notif.id);
                setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
                setUnreadCount(prev => Math.max(0, prev - 1));
            } catch (err) {}
        }
        if (notif.link) navigate(notif.link);
    };

    const handleLogout = () => {
        handleMenuClose();
        logout();
    };

    const handleBackToCategories = () => {
        clearCategory();
        navigate('/categories');
    };

    /* ── Super admin: the active category's items. Everyone else: all their pages, own service first ── */
    const menuItems = superAdmin
        ? (activeCategory ? ALL_MENU_ITEMS.filter(item => item.category === activeCategory) : ALL_MENU_ITEMS)
        : ALL_MENU_ITEMS.filter(item => canOpenPath(roles, item.path))
            .sort((a, b) => menuRank(roles, a.path) - menuRank(roles, b.path))
            // Several services in one list: "Bookings" / "Configuration" get their service in front.
            .map((item, _, all) => (all.filter(o => o.text === item.text).length > 1
                ? { ...item, text: `${CATEGORY_LABELS[item.category]?.label ?? item.category} — ${item.text}` }
                : item));

    const categoryMeta = superAdmin
        ? (activeCategory ? CATEGORY_LABELS[activeCategory] : null)
        : { label: roleLabel, gradient: 'linear-gradient(135deg, #475569, #1e293b)' };

    const getPageTitle = () => findMenuItemForPath(location.pathname)?.text || 'Admin Panel';

    const drawer = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Toolbar sx={{ px: [2], display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', height: 40 }}>
                    <Box
                        component="img"
                        src={yannLogo}
                        alt="YANN"
                        sx={{ height: '100%', width: 'auto', display: 'block' }}
                    />
                </Box>
                {isMobile && (
                    <IconButton onClick={handleDrawerToggle}>
                        <ChevronLeftIcon />
                    </IconButton>
                )}
            </Toolbar>
            <Divider />

            {/* ── Back to Categories button (marketing has one page, no hub) ── */}
            {activeCategory && superAdmin && (
                <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
                    <ListItemButton
                        onClick={handleBackToCategories}
                        sx={{
                            borderRadius: 1.5,
                            py: 1,
                            px: 1.5,
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                            },
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 32, color: 'primary.main' }}>
                            <ArrowBackIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                            primary="All Categories"
                            primaryTypographyProps={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: 'primary.main',
                            }}
                        />
                        <GridViewIcon sx={{ fontSize: 16, color: 'primary.main', opacity: 0.6 }} />
                    </ListItemButton>
                </Box>
            )}

            {/* ── Category header ──────────────────────────────────── */}
            {categoryMeta && (
                <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                    <Box
                        sx={{
                            background: categoryMeta.gradient,
                            borderRadius: 2,
                            px: 2,
                            py: 1.5,
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                letterSpacing: '0.01em',
                            }}
                        >
                            {categoryMeta.label}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                color: alpha('#fff', 0.7),
                                fontSize: '0.7rem',
                            }}
                        >
                            {menuItems.length} {menuItems.length === 1 ? 'module' : 'modules'}
                        </Typography>
                    </Box>
                </Box>
            )}

            <List sx={{ flexGrow: 1, px: 1, pt: categoryMeta ? 0.5 : 2 }}>
                {menuItems.map((item, idx) => (
                    <ListItem
                        key={item.text}
                        disablePadding
                        sx={{
                            mb: 0.5,
                            animation: activeCategory ? `sidebarItemSlideIn 0.3s ease-out ${idx * 0.04}s both` : 'none',
                        }}
                    >
                        <ListItemButton
                            component={NavLink}
                            to={item.path}
                            onClick={() => isMobile && setMobileOpen(false)}
                            sx={{
                                borderRadius: 1,
                                px: 2,
                                '&.active': {
                                    bgcolor: 'primary.light',
                                    color: 'primary.main',
                                    '& .MuiListItemIcon-root': {
                                        color: 'primary.main',
                                    },
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.text}
                                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
            <Divider />
            <Box sx={{ p: 2 }}>
                <ListItemButton
                    onClick={handleLogout}
                    sx={{
                        borderRadius: 1,
                        color: 'error.main',
                        '&:hover': {
                            bgcolor: alpha(theme.palette.error.main, 0.08),
                        },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText
                        primary="Logout"
                        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
                    />
                </ListItemButton>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <AppBar
                position="fixed"
                sx={{
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    ml: { md: `${drawerWidth}px` },
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    boxShadow: 'none',
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ mr: 2, display: { md: 'none' } }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
                            {getPageTitle()}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                        {superAdmin && (
                            <Tooltip title="Notifications">
                                <IconButton size="large" color="inherit" onClick={handleNotifMenuOpen}>
                                    <Badge badgeContent={unreadCount} color="error">
                                        <BellIcon />
                                    </Badge>
                                </IconButton>
                            </Tooltip>
                        )}

                        <Menu
                            anchorEl={notifAnchorEl}
                            open={Boolean(notifAnchorEl)}
                            onClose={handleMenuClose}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            PaperProps={{
                                sx: { width: 360, maxHeight: 480, mt: 1.5, borderRadius: 2, boxShadow: theme.shadows[10] }
                            }}
                        >
                            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1" fontWeight={700}>Notifications</Typography>
                                <Chip label={`${unreadCount} New`} size="small" color="error" variant="filled" sx={{ fontWeight: 600, height: 20 }} />
                            </Box>
                            <Divider />
                            <List sx={{ p: 0, maxHeight: 350, overflow: 'auto' }}>
                                {notifications.length === 0 ? (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">No new alerts</Typography>
                                    </Box>
                                ) : (
                                    notifications.map((n) => (
                                        <MenuItem 
                                            key={n.id} 
                                            onClick={() => handleNotifClick(n)}
                                            sx={{ 
                                                py: 1.5, 
                                                px: 2, 
                                                borderBottom: '1px solid', 
                                                borderColor: 'divider',
                                                bgcolor: n.is_read ? 'transparent' : alpha(theme.palette.primary.main, 0.04),
                                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) }
                                            }}
                                        >
                                            <Box sx={{ width: '100%' }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="subtitle2" fontWeight={n.is_read ? 500 : 700} color={n.type === 'error' ? 'error.main' : 'text.primary'}>
                                                        {n.title}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatTimeAgo(new Date(n.created_at))}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ 
                                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' 
                                                }}>
                                                    {n.message}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))
                                ) }
                            </List>
                            <Divider />
                            <Box sx={{ p: 1, textAlign: 'center' }}>
                                <Button 
                                    fullWidth 
                                    size="small" 
                                    onClick={() => { handleMenuClose(); navigate('/notifications'); }}
                                    sx={{ fontWeight: 600 }}
                                >
                                    View All Notifications
                                </Button>
                            </Box>
                        </Menu>

                        {superAdmin && <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'right' }}>
                                <Typography variant="subtitle2" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
                                    {personName || roleLabel}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {personName ? `${roleLabel} · ${admin?.email || ''}` : (admin?.email || 'admin@scankaro.com')}
                                </Typography>
                            </Box>
                            <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0 }}>
                                <Avatar
                                    sx={{
                                        bgcolor: 'primary.light',
                                        color: 'primary.main',
                                        fontWeight: 'bold',
                                        width: 40,
                                        height: 40,
                                        border: '1px solid',
                                        borderColor: 'primary.main'
                                    }}
                                >
                                    {roleInitials}
                                </Avatar>
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            >
                                <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
                                <MenuItem onClick={handleMenuClose}>My Account</MenuItem>
                                <Divider />
                                <MenuItem onClick={handleLogout}>Logout</MenuItem>
                            </Menu>
                        </Box>
                    </Box>
                </Toolbar>
            </AppBar>

            <Box
                component="nav"
                sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 2, sm: 3 },
                    width: { md: `calc(100% - ${drawerWidth}px)` },
                    mt: '64px', // Height of AppBar
                    bgcolor: 'background.default',
                    animation: 'contentFadeIn 0.4s ease-out both',
                }}
            >
                <Suspense fallback={<TablePageSkeleton />}>
                    <Outlet />
                </Suspense>
            </Box>
        </Box>
    );
};

export default DashboardLayout;
